import {NextPage} from 'next';
import EventXPage from 'modules/events/pages/EventX';
import {withBasicDataServerSide} from 'core/next/controllers';
import {eventXPageController} from 'modules/events/controller/eventXPageController';
import {IProductShortResponse} from 'modules/products/models';
import {eventsPageController} from 'modules/events/controller';
import React, {FC} from 'react';
import {IEventsResponseData} from 'modules/events/types';
import {NoEventsInCity} from 'modules/events/components/NoEventsInCity';
import {EventsListProvider} from 'modules/events/provider';
import {IndividualTastingProvider} from 'modules/events/provider/IndividualTasting';
import {Events} from 'modules/events/pages/Events';
import {PageHead} from 'core/modules/PageHead';
import {metaData} from 'core/modules/PageHead/constants';
import {ICity} from 'modules/locations/models';
import {findCityByCode} from 'modules/locations/helpers';
import {get404Exception} from 'core/exceptions/NotFoundException';
import {formatTastingDescription, formatTastingTitle} from 'modules/events/helpers';
import {useRouter} from 'next/router';
import {useGetEventQuery} from 'modules/events/services';
import {IBreadcrumb} from 'modules/seo/types';

interface IProps {
    pageProps: {
        tastingProductsData?: IProductShortResponse;
        data?: IEventsResponseData | null;
        city?: ICity;
        breadcrumbs?: IBreadcrumb[];
    };
}

const TastingPageHead: FC = () => {
    const {query} = useRouter();
    const {data} = useGetEventQuery({id: query.slug as string});
    const event = data?.data;
    const seo = data?.seo;

    if (!event) {
        return null;
    }

    const title = formatTastingTitle(event.title, event.eventDate, event.address, event.winery.metroStations);
    const description = formatTastingDescription(event.title, event.previewDescription);

    return (
        <PageHead
            title={seo?.metaTitle || title}
            description={seo?.metaDescription || description}
            robots={seo?.robots}
        />
    );
};

const EventX: NextPage<IProps> = ({pageProps}) => {
    const {tastingProductsData, breadcrumbs = [], data, city} = pageProps;

    if (tastingProductsData) {
        return (
            <>
                <TastingPageHead />
                <EventXPage tastingProductsData={tastingProductsData} breadcrumbs={breadcrumbs} />
            </>
        );
    }

    // если это страница дегустаций по городу
    const renderContent = () => {
        if (null === data) {
            return <NoEventsInCity breadcrumbs={breadcrumbs} />;
        }

        return (
            <EventsListProvider defaultData={data}>
                <IndividualTastingProvider>
                    <Events city={city} breadcrumbs={breadcrumbs} />
                </IndividualTastingProvider>
            </EventsListProvider>
        );
    };

    return (
        <>
            <PageHead
                title={metaData.events.title(city?.title)}
                description={metaData.events.description(city?.title)}
            />
            {renderContent()}
        </>
    );
};

export const getServerSideProps = withBasicDataServerSide(async (store, ctx) => {
    const {slug} = ctx.query || {};

    // Получаем справочник городов
    const cities = store.getState().locations.cities as ICity[];

    // Находим город по slug
    const city = findCityByCode(cities, 'string' === typeof slug ? slug : undefined);

    const isNumericSlug = 'string' === typeof slug && /^\d+$/u.test(slug);

    // если города нет в слова и слаг как айди ивента - пытаемся запросить ивент
    if (!city && isNumericSlug) {
        const {tastingProductsData, breadcrumbs = []} = await eventXPageController(store, slug as string);

        return {
            props: {
                tastingProductsData,
                breadcrumbs,
            },
        };
    }
    // запрашиваем дегустации для города, который был найден в словаре
    if (city) {
        const result = await eventsPageController(store, {
            ...ctx,
            cityCode: city.code.toLowerCase(),
        });
        return {
            props: {
                data: result,
                city,
                breadcrumbs: result?.breadcrumbs ?? [],
            },
        };
    }
    // если города нет в словаре - 404
    throw get404Exception();
});

export default EventX;
